/**
 * Helper functions for consistent zone ID conversions between device settings,
 * scene storage, and UI representation for Presence Device (S4SN-0U61X)
 *
 * Zone ID mapping:
 * - Device settings: presencezone:201, presencezone:202, etc.
 * - Scene storage: prsn_st4202, prsn_nobj4202 (device zone + 1 + '4' prefix)
 * - UI representation: zone_201, zone_202 (device zone IDs)
 */
import { ILLUMINATION_BANDS } from 'src/components/Scenes/helpers/init_device_conditions';
import type { DeviceConditionUI } from 'src/types/DeviceCondAndTriggerUI';
import { real_id } from 'src/helpers/interface_helpers';
import { isPresenceDevice } from 'src/components/Scenes/helpers/device_conditions/custom_cases/presence/presence-device-helpers';
import { deviceZoneIdToSceneZoneId, isPresenceZoneCondition } from 'src/helpers/presence_scene_tokens';
export {
	buildPresenceConditionName,
	deviceZoneIdToSceneZoneId,
	extractZoneIdFromPresenceCondition,
	getPresenceConditionType,
	isPresenceZoneCondition,
	sceneZoneIdToDeviceZoneId,
} from 'src/helpers/presence_scene_tokens';

/**
 * Convert device zone ID to scene zone ID for storage
 * @param deviceZoneId - Zone ID from device settings (e.g., "201")
 * @returns Scene zone ID (e.g., "202")
 */

/**
 * Whether the device-condition wizard should use the reactive presence edit bootstrap
 * (S4SN, zone rows from settings, or UI cond names that map to presence zones).
 */
export function shouldUsePresenceEditBootstrap(
	tr: DeviceConditionUI | undefined,
	devices: Record<string, unknown>,
	deviceSettings: Record<string, unknown> | undefined,
): boolean {
	if (!tr?.device?.id) return false;
	const baseId = real_id(tr.device.id);
	if (isPresenceDevice(baseId, devices)) return true;
	const cn = tr.cond_name ?? '';
	if (cn.startsWith('presence_zone_')) return true;
	const settings = deviceSettings ?? {};
	return Object.keys(settings).some((k) => /^presencezone:\d+$/.test(k));
}

/**
 * Fingerprint for presence edit bootstrap — re-run when zones load, device appears,
 * or translated condition identity changes (avoids resetting unrelated user edits).
 */
export function getPresenceEditBootstrapKey(
	tr: DeviceConditionUI | undefined,
	devices: Record<string, unknown>,
	deviceSettings: Record<string, unknown> | undefined,
): string {
	if (!tr?.device?.id) return '';
	const baseIdEarly = real_id(tr.device.id);
	const settingsForDevice = deviceSettings ?? {};
	const zoneSig = Object.keys(settingsForDevice)
		.filter((k) => /^presencezone:\d+$/.test(k))
		.sort()
		.join(',');
	const storeDeviceMark = devices[baseIdEarly] ? '1' : '0';
	return [
		baseIdEarly,
		tr.cond_name ?? '',
		String(tr.add_info_ind ?? ''),
		String(tr.input_index ?? ''),
		zoneSig,
		storeDeviceMark,
	].join('|');
}

/**
 * Normalize presence condition value to the expected UI format
 * @param value - Value from scene data (could be "0", "1", "true", "false", true, false, 0, 1)
 * @param conditionType - "state" or "object_count"
 * @returns Normalized value for UI use
 */
export function normalizePresenceConditionValue(
	value: any,
	conditionType: 'state' | 'object_count',
): any {
	if (conditionType === 'state') {
		// Convert numeric and string representations to boolean strings for state conditions
		if (value === '1' || value === 1 || value === true || value === 'true') {
			return 'true';
		} else if (value === '0' || value === 0 || value === false || value === 'false') {
			return 'false';
		}
		// Return as-is if already in correct format
		return value;
	} else if (conditionType === 'object_count') {
		// Ensure numeric value for object count conditions
		return typeof value === 'number' ? value : Number(value);
	}

	return value;
}

/**
 * Get the condition type from a presence condition name
 * @param conditionName - Full condition name (e.g., "prsn_st4202")
 * @returns "state" for presence/absence conditions, "object_count" for count conditions, "delta" for enter/leave delta conditions, or null
 */

/**
 * Validate presence condition data and log any issues
 * @param conditionData - Condition data to validate
 * @param context - Context string for logging
 * @returns True if valid, false if invalid
 */
export function validatePresenceConditionData(
	conditionData: {
		conditionName?: string;
		deviceZoneId?: string;
		value?: any;
		operator?: string;
	},
	context: string,
): boolean {
	const { conditionName, deviceZoneId, value, operator } = conditionData;
	let isValid = true;
	const issues: string[] = [];

	// Check condition name
	if (!conditionName) {
		issues.push('Missing condition name');
		isValid = false;
	} else if (
		!isPresenceZoneCondition(conditionName) &&
		conditionName !== 'presence_zone_state' &&
		conditionName !== 'presence_zone_objects' &&
		conditionName !== 'presence_zone_objects_delta' &&
		conditionName !== 'luxury' &&
		conditionName !== 'lux_i' && // legacy numeric — still used when reading old scenes
		conditionName !== 'illumination'
	) {
		issues.push(`Invalid condition name: ${conditionName}`);
		isValid = false;
	}

	// Check device zone ID - not required for luminosity / illuminance conditions
	if (conditionName !== 'luxury' && conditionName !== 'lux_i' && conditionName !== 'illumination') {
		if (!deviceZoneId) {
			issues.push('Missing device zone ID');
			isValid = false;
		} else if (isNaN(Number(deviceZoneId))) {
			issues.push(`Invalid device zone ID format: ${deviceZoneId}`);
			isValid = false;
		}
	}

	// Check value based on condition type
	if (conditionName === 'presence_zone_state' || conditionName?.startsWith('prsn_st4')) {
		// Accept boolean values (true/false) and numeric representations (0/1, "0"/"1")
		const isValidBooleanValue =
			value === 'true' ||
			value === 'false' ||
			value === true ||
			value === false ||
			value === '0' ||
			value === '1' ||
			value === 0 ||
			value === 1;

		if (!isValidBooleanValue) {
			issues.push(`Invalid state condition value: ${value} (expected true/false or 0/1)`);
			isValid = false;
		}
	} else if (conditionName === 'presence_zone_objects' || conditionName?.startsWith('prsn_nobj4')) {
		if (typeof value !== 'number' && isNaN(Number(value))) {
			issues.push(`Invalid object count value: ${value} (expected number)`);
			isValid = false;
		}
	} else if (
		conditionName === 'presence_zone_objects_delta' ||
		conditionName?.startsWith('prsn_nobj_d4')
	) {
		if (typeof value !== 'number' && isNaN(Number(value))) {
			issues.push(`Invalid delta value: ${value} (expected number)`);
			isValid = false;
		}
	} else if (conditionName === 'luxury') {
		// Discrete illumination band: must be one of dark / twilight / bright
		if (!ILLUMINATION_BANDS.includes(String(value))) {
			issues.push(
				`Invalid luminosity band: ${value} (expected one of ${ILLUMINATION_BANDS.join(', ')})`,
			);
			isValid = false;
		}
	} else if (conditionName === 'lux_i' || conditionName === 'illumination') {
		// Numeric lux (`lux_i` in scene service)
		if (typeof value !== 'number' && isNaN(Number(value))) {
			issues.push(`Invalid illuminance value: ${value} (expected number)`);
			isValid = false;
		} else if (Number(value) < 0) {
			issues.push(`Invalid illuminance value: ${value} (expected non-negative number)`);
			isValid = false;
		}
	}

	// Check operator (`mt` = more-than alias used by some scene payloads for lux)
	const opNorm =
		(conditionName === 'lux_i' || conditionName === 'illumination') && operator === 'mt'
			? 'gt'
			: operator;
	if (!opNorm) {
		issues.push('Missing operator');
		isValid = false;
	} else if (!['eq', 'gt', 'lt'].includes(opNorm)) {
		issues.push(`Invalid operator: ${operator}`);
		isValid = false;
	}

	// Log validation results
	if (!isValid) {
		console.error(`Presence condition validation failed [${context}]:`, {
			conditionData,
			issues,
		});
	} else if (import.meta?.env?.DEV) {
		console.log(`Presence condition validation passed [${context}]:`, conditionData);
	}

	return isValid;
}

/**
 * Get zone name from zone ID and device ID
 * @param zoneId - Zone ID to look up
 * @param deviceId - Device ID to get settings from
 * @param devicesSettings - Device settings store (pass get(devices_settings))
 * @returns Zone name or fallback to "Zone {zoneId}"
 */
export function getPresenceZoneName(zoneId: string, deviceId?: string, devicesSettings?: any): string {
	if (!deviceId || !zoneId || !devicesSettings) return `Zone ${zoneId}`;

	const deviceSettingsData = devicesSettings[deviceId];
	const zoneKey = `presencezone:${zoneId}`;
	const zoneData = deviceSettingsData?.[zoneKey];
	return zoneData?.name || `Zone ${zoneId}`;
}
